import { Component, Prop } from 'vue-property-decorator';
import { BaseComponent } from '../base-component';
import { LocationDTO } from "../../model/LocationDTO";
import { LocationStatsDTO } from '../../model/LocationStatsDTO';
import { formatDateLocalISO } from '@/helpers/dates';
import { ApartmentSearchFilterDTO } from '../../model/ApartmentSearchFilterDTO';

import { groupBy, sortBy, union, difference, range, flatten, unionBy } from 'lodash-es';

interface FormValues {
	locationIds: number[];
	maxPrice: number;
	layouts: number[];
	completionDate?: Date;
}

@Component
export class SearchPanel extends BaseComponent {
	@Prop({default: true}) showLocationSelect: boolean;
	@Prop() locations: LocationDTO[];
	@Prop() locationStats: LocationStatsDTO[];

	formValues: FormValues = {
		locationIds: [],
		maxPrice: 0,
		layouts: [],
		completionDate: null
	};

	showLocationDropdown: boolean = false;
	showPriceDropdown: boolean = false;
	showLayoutDropdown: boolean = false;
	showCompletionDateDropdown: boolean = false;

	mounted() {
		this.formValues.maxPrice = this.maxPriceOption;
		this.formValues.completionDate = this.latestCompletionDateOption;

		if (!this.showLocationSelect) {
			this.formValues.locationIds.push(this.locations[0].id);
		}
	}

	/* Locations */

	get cities() {
		return groupBy(sortBy(this.locations, 'cityOrder'), 'city');
	}

	handleCityIdChange(event: Event) {
		const { value, checked } = event.target as HTMLInputElement;
		this.toggleLocations(
			this.locations.filter(l => l.cityId === parseInt(value)).map(l => l.id),
			checked
		);
	}

	private toggleLocations(locationIds: number[], select: boolean) {
		this.formValues.locationIds = select
			? union(this.formValues.locationIds, locationIds)
			: difference(this.formValues.locationIds, locationIds);
	}

	/* Price */

	get minPriceOption() {
		return Math.floor(Math.min(...this.locationStats.map(s => s.minAvailablePrice)) / 1e6) * 1e6;
	}

	get maxPriceOption() {
		return Math.ceil(Math.max(...this.locationStats.map(s => s.maxAvailablePrice)) / 1e6) * 1e6;
	}

	get priceOptions() {
		// step by 1m from min to 6m
		let priceRange = range(
			this.minPriceOption + 1e6, // options are upper bound, skip min price
			Math.min(this.maxPriceOption, 6e6),
			1e6
		);

		// step by 2m from 6m up to 12m
		if (this.maxPriceOption > 5e6) {
			priceRange = priceRange.concat(range(6e6, Math.min(this.maxPriceOption, 12e6), 2e6));
		}

		// step by 5m from 12m up to max (not included)
		if (this.maxPriceOption > 10e6) {
			priceRange = priceRange.concat(range(12e6, this.maxPriceOption, 5e6));
		}

		// add max replace first lower if too close
		if (this.maxPriceOption - priceRange[priceRange.length - 1] < 5e6 / 2) {
			priceRange[priceRange.length - 1] = this.maxPriceOption;
		} else {
			priceRange.push(this.maxPriceOption);
		}

		return priceRange;
	}

	/* layout */

	get layoutOptions() {
		const layoutStats = this.locationStats.reduce((layoutStats, s) =>
			unionBy(layoutStats, s.layoutStats, 'layoutId').filter(f => !f.sold)
			, []);

		const sortedStats = layoutStats.sort((a, b) => a.layoutLabel.localeCompare(b.layoutLabel))

		return sortedStats.map(l => ({
			label: l.layoutLabel,
			value: l.layoutRoomCount
		}));
	}

	/* Completion date */

	get completionDateOptions() {
		if (!this.locationStats || this.allCompletedApartments) {
			return [];
		}

		const locationLastCompletionDates = this.locationStats.map(s => s.lastApartmentCompletionDate).sort().reverse();
		const lastCompletionDate = new Date(locationLastCompletionDates[0]);

		const locationNextCompletionDates = this.locationStats.map(s => s.nextApartmentCompletionDate).sort();
		const nextCompletionDate = new Date(locationNextCompletionDates[0]);

		console.log(nextCompletionDate);

		const currentDate = new Date() > new Date(nextCompletionDate) ? new Date() : new Date(nextCompletionDate);
		const yearRange = range(currentDate.getFullYear(), lastCompletionDate.getFullYear() + 1);
		const options = flatten(
			yearRange.map(year => [
				new Date(year, 5, 30), // half of a year (30th June)
				new Date(year, 11, 31) // end of a year (31th December)
			])
		);

		if (currentDate.getMonth() > 5) {
			// remove half of current year option if today is at least July
			options.shift();
		}

		if (lastCompletionDate.getMonth() <= 5) {
			// remove end of max complention year option if max completion date is at most June
			options.pop();
		}

		return options;
	}

	get allCompletedApartments() {
		return this.locationStats.filter(f => !f.allApartmentsCompleted).length === 0;
	}

	get existCompletedApartment() {
		return this.locationStats.filter(f => f.availableCompletedApartmentCount > 0).length > 0;
	}

	get latestCompletionDateOption() {
		return this.completionDateOptions[this.completionDateOptions.length - 1];
	}

	get allCompletionDateOption() {
		return new Date(2999, 11, 31);
	}

	/* Submit */

	submit() {

		let queryString: ApartmentSearchFilterDTO = {};

		if (this.formValues.locationIds.length > 0) {
			queryString.locationIds = this.formValues.locationIds;
		}

		if (this.formValues.maxPrice && this.formValues.maxPrice !== this.maxPriceOption) {
			queryString.maxPrice = this.formValues.maxPrice;
		}

		if (this.formValues.layouts.length > 0) {
			queryString.layoutRoomCounts = this.formValues.layouts;
		}

		if (!this.formValues.completionDate && this.existCompletedApartment) {
			queryString.completedOnly = true;
		} else if (!this.formValues.completionDate) {
			queryString.completionDate = formatDateLocalISO(this.latestCompletionDateOption);
		} else if (this.formValues.completionDate.getTime() !== this.latestCompletionDateOption.getTime()) {
			queryString.completionDate = formatDateLocalISO(this.formValues.completionDate);
		}

		if (this.$route.query.csh) {
			queryString.csh = parseInt(this.$route.query.csh);
		}

		/* Preview (for showing preparation time version) */
		if (this.$route.query.predbezne) {
			queryString.predbezne = parseInt(this.$route.query.predbezne);
		}

		this.$router.push({
			name: 'apartment-search',
			query: {
				...queryString as any,
				search: true
			}
		});
	}
}

export default SearchPanel;
